import { useEffect, useMemo } from "react";
import { MapPin } from "lucide-react";
import Select2 from "./Select2";
import useMyLocations from "../hooks/useMyLocations";

/**
 * The location an entry belongs to, or the location a register shows.
 *
 * mode="entry"  — picks one of the login's active locations; starts on the
 *                 login's default. A login with a single location sees it
 *                 as a fixed label.
 * mode="filter" — adds "All locations" (value "") for a login that works in
 *                 more than one location; a login with a single location
 *                 gets nothing to pick, and the component renders nothing.
 *
 * `value` / `onChange` carry the location id as a string ("" = all / none).
 */
export default function WorkLocationSelect({
  value, onChange, mode = "entry", label, disabled = false, style, minWidth = 180,
}) {
  const { locations, defaultLocationId, loading } = useMyLocations();
  const usable = useMemo(
    () => (mode === "entry" ? locations.filter(l => l.isActive) : locations),
    [locations, mode]);

  // An entry starts on the login's default location.
  useEffect(() => {
    if (mode !== "entry" || loading || value) return;
    const def = usable.find(l => l.id === defaultLocationId) || usable[0];
    if (def) onChange(String(def.id));
  }, [mode, loading, value, usable, defaultLocationId, onChange]);

  if (mode === "filter" && usable.length <= 1) return null;

  if (mode === "entry" && usable.length === 1) {
    return (
      <div style={style}>
        {label && <div style={labelStyle}>{label}</div>}
        <div style={fixedStyle} title="Your location">
          <MapPin size={13} style={{ color: "var(--primary)" }} />
          {usable[0].locationName}
        </div>
      </div>
    );
  }

  const options = [
    ...(mode === "filter" ? [{ value: "", label: "All locations" }] : []),
    ...usable.map(l => ({
      value: String(l.id),
      label: l.locationName + (l.isMain ? " (main)" : "") + (l.isActive ? "" : " · inactive"),
    })),
  ];

  return (
    <div style={{ minWidth, ...style }}>
      <Select2
        label={label}
        value={value || ""}
        onChange={v => onChange(v ? String(v) : "")}
        options={options}
        disabled={disabled || loading}
        placeholder={mode === "filter" ? "All locations" : "Select location"}
      />
    </div>
  );
}

const labelStyle = { fontSize: 13, fontWeight: 600, color: "var(--text-1)", marginBottom: 5 };
const fixedStyle = {
  display: "flex", alignItems: "center", gap: 6, padding: "7px 10px", borderRadius: 7,
  border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-0)",
  fontSize: 14, fontWeight: 600,
};
